import { HeaderTabItem } from '@vtblife/layout-config/types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Typography } from '@vtblife/uikit';

import cn from 'classnames';

import Icon from '@vtblife/uikit-icons';

import { useAccordion } from '../tabs-for-public-area-mobile/use-accordion';
import { sendAnalyticsEvent } from '../../analytics';
import styles from './menu-items-for-tabs-public-mobile.module.css';
import { isActivePath } from '../utils';

export const MenuItemForTabsPublicMobile = ({ item, pathname }: { item: HeaderTabItem; pathname?: string }) => {
    const { displayName, displayNameMobile, url, path, dropdownMenu } = item;
    const href = path || url;
    const detailsRef = useRef<HTMLDetailsElement>(null);
    const { toggle } = useAccordion(detailsRef);
    const [isOpen, setIsOpen] = useState(false);

    const displayTitle = displayNameMobile || displayName;
    const isParentActive = isActivePath(pathname, href);

    const hasActiveSubItem = useMemo(() => {
        if (!dropdownMenu) return false;

        return dropdownMenu.some((subItem) => {
            const subHref = subItem.path || subItem.url;
            return isActivePath(pathname, subHref);
        });
    }, [dropdownMenu, pathname]);

    useEffect(() => {
        if (hasActiveSubItem && detailsRef.current) {
            detailsRef.current.open = true;
            setIsOpen(true);
        }
    }, [hasActiveSubItem]);

    const handleClickItemWithDropdown = useCallback(
        (e) => {
            e.preventDefault();
            setIsOpen((prev) => !prev);
            toggle();
            sendAnalyticsEvent({
                event: 'header_click',
                user_type: displayTitle,
                product_type: displayTitle,
            });
        },
        [displayTitle, toggle],
    );

    const handleClickItemWithoutDropdown = useCallback(() => {
        sendAnalyticsEvent({
            event: 'header_click',
            user_type: displayTitle,
            product_type: displayTitle,
        });
    }, [displayTitle]);

    const handleClickSubItem = useCallback(
        (subItem: HeaderTabItem) => () => {
            const subTitle = subItem.displayNameMobile || subItem.displayName;
            sendAnalyticsEvent({
                event: 'header_click',
                user_type: subTitle,
                product_type: subTitle,
            });
        },
        [],
    );

    if (!dropdownMenu || dropdownMenu.length === 0) {
        return (
            <div className={styles.menuItem}>
                <a href={href} className={styles.menuItemHeading} onClick={handleClickItemWithoutDropdown}>
                    <Typography variant="primary-alone" bold>
                        <span className={cn({ [styles.menuItemLinkActive]: isParentActive })}>
                            {displayNameMobile || displayName}
                        </span>
                    </Typography>
                </a>
            </div>
        );
    }

    return (
        <details ref={detailsRef} className={styles.menuItem}>
            <summary className={styles.menuItemHeading} onClick={handleClickItemWithDropdown}>
                <Typography variant="primary-alone" bold>
                    {displayNameMobile || displayName}
                </Typography>
                <div className={cn(styles.icon, { [styles.iconRotated]: isOpen })}>
                    <Icon name="chevron-down" size="s" />
                </div>
            </summary>
            <div data-content className={styles.menuItemDropdown}>
                {dropdownMenu.map((subItem) => {
                    const subHref = subItem.path || subItem.url;
                    const isSubItemActive = pathname === subHref;

                    return (
                        <a
                            key={subItem.id}
                            href={subHref}
                            className={cn(styles.menuItemDropdownItem, {
                                [styles.menuItemDropdownItemActive]: isSubItemActive,
                            })}
                            onClick={handleClickSubItem(subItem)}
                        >
                            <Typography variant="primary-alone">
                                {subItem.displayNameMobile || subItem.displayName}
                            </Typography>
                        </a>
                    );
                })}
            </div>
        </details>
    );
};
